import type {
  CaptureEventOptions,
  NotificationPriority,
  OnboardingAnalyticsEvent,
  RedirectToTeamGameErrorCode,
  ReferralNotificationTemplate,
  RewardData,
} from "@textclub/common/bridge";
import type { EntrySource } from "@textclub/common/entry-source";
import type {
  AvatarSize,
  CustomTextMessage,
  PlayerProfile,
  PurchaseData,
  RichMessage,
  SubscriptionData,
} from "@textclub/common/types";

import type { GetLoginLinkResponseErrors } from "./sdk-client";
import { Sdk } from "./sdk-client";

export type { CaptureEventOptions, RewardData } from "@textclub/common/bridge";
export type { EntrySource } from "@textclub/common/entry-source";

/** @internal */
export type NameValidationResult =
  | {
      status: "valid";
    }
  | {
      status: "invalid";
      validationError: "too_short" | "too_long" | "inappropriate";
    };

export type LifecycleListener = () => void | PromiseLike<void>;

/**
 * - `vibrate`: the browser has `navigator.vibrate` (Android, and desktop Chromium).
 * - `tap`: iPhone on iOS 18 or later; a tap that asked for a vibration ticks on release.
 * - `none`: no haptics.
 */
export type HapticsCapability = "vibrate" | "tap" | "none";

export interface HapticsModule {
  /**
   * Beta. Same as `navigator.vibrate()`, but also works on iPhone, where it plays one native
   * tick when the current tap on the game canvas is released. On iPhone only a call made
   * during that tap counts (while the finger is down, or synchronously in a `touchend`,
   * `pointerup` or `click` handler), and the first call installs a layer, so its tap
   * does not tick.
   *
   * @returns Whether the request was accepted; on iPhone, whether the current tap ticks.
   */
  vibrate(pattern: number | number[]): boolean;

  /** Reports which kind of haptics {@link HapticsModule.vibrate} produces on this device. */
  getCapability(): HapticsCapability;
}

export interface LifecycleModule {
  /** Runs when the game document changes from visible to hidden. */
  onHide(listener: LifecycleListener): () => void;

  /** Runs when the game document changes from hidden to visible. */
  onShow(listener: LifecycleListener): () => void;

  /** Runs when the platform begins an exit flow for the game. */
  onExitRequested(listener: LifecycleListener): () => void;
}

/**
 * The JestSDK runtime API, exposed as a global when the SDK script is
 * loaded from `https://cdn.jest.com/sdk/latest/jestsdk.js`.
 *
 * Call {@link Jest.init} once on startup, then use the methods below to
 * interact with the player, schedule notifications, sell products, and
 * track referrals. Most methods throw if called before initialization.
 *
 * Docs: https://docs.jest.com/sdk/html5
 */
export interface JestSDK {
  /**
   * Resolves once the SDK is fully initialized and player data is loaded.
   *
   * `init()` already returns this promise — call `isReady()` only when
   * you need to await initialization from a different code path that
   * doesn't have access to the original `init()` promise.
   *
   * Safe to call any time after `init()` has been invoked, including before
   * `init()` resolves; it chains off the same in-flight initialization.
   *
   * @throws {Error} If initialization times out or `init()` was never called.
   */
  isReady(): Promise<void>;

  /**
   * Returns the entry payload for this game session, or an empty object
   * if no payload was supplied.
   *
   * The entry payload is arbitrary metadata attached to the link the
   * player used to enter the game. Common sources include:
   * - Referral links (from `referrals.shareReferralLink`)
   * - Notification links (from `notifications.scheduleNotification`)
   * - Onboarding game handoffs
   *
   * Typical use cases: difficulty selection, referral attribution,
   * restoring context after registration, A/B test variants.
   *
   * @returns The entry payload as a JSON object (always a `Record`, never null).
   * @throws {Error} If the SDK is not initialized.
   * @example
   * ```typescript
   * const payload = JestSDK.getEntryPayload();
   * const difficulty = payload.difficulty ?? "normal";
   * ```
   */
  getEntryPayload(): Record<string, unknown>;

  /**
   * Returns how the player entered this game session.
   *
   * Entry sources describe the current entry, rather than the player's
   * first acquisition. Unclassified entries return `unknown`.
   * Standalone mock mode returns `direct`.
   *
   * @throws {Error} If SDK initialization has not completed.
   */
  getEntrySource(): EntrySource;

  /**
   * Returns the current player's identity.
   *
   * Each player has a `playerId` that is stable per-game and persists
   * across sessions and devices, including when a guest later registers.
   * Use this to key your own player state.
   *
   * - `registered: false` — guest player. Cannot receive notifications.
   *   Prompt registration via `login()` or `showRegistrationOverlay()`.
   * - `registered: true` — has a Jest account. `username` and `avatarUrl`
   *   are platform values you may use in your UI.
   *
   * @returns The player object. `username`/`avatarUrl` are `null` for guests.
   * @throws {Error} If the SDK is not initialized.
   * @example
   * ```typescript
   * const player = JestSDK.getPlayer();
   * if (!player.registered) {
   *   // Guest — consider prompting registration
   * }
   * ```
   */
  getPlayer(): {
    playerId: string;
    registered: boolean;
    username: string | null;
    avatarUrl: string | null;
  };

  /**
   * Returns a signed player payload for server-side verification.
   *
   * Use this when your game has a backend and needs to authenticate
   * the player for server requests. `playerSigned` is a JWS (HS256)
   * signed with your game's shared secret (configured in the Developer
   * Console → Games → Secrets).
   *
   * Verify it on your backend with any standard JWT library. The
   * decoded payload has the shape:
   * ```ts
   * {
   *   player: { playerId, registered, username, avatarUrl };
   *   iat: number;  // issued-at timestamp
   *   aud: string;  // game id
   *   sub: string;  // player id
   * }
   * ```
   *
   * Jest does not set an explicit expiration; reject tokens older than
   * a chosen threshold (e.g. 24h) and request a new one when needed.
   *
   * Works for both registered and guest players.
   *
   * Docs: https://docs.jest.com/sdk/player?engine=html5#get-a-signed-player-payload
   *
   * @example Authenticate a backend request
   * ```typescript
   * const { playerSigned } = await JestSDK.getPlayerSigned();
   * await fetch("/api/save-progress", {
   *   method: "POST",
   *   headers: { authorization: `Bearer ${playerSigned}` },
   *   body: JSON.stringify({ score: 1500 }),
   * });
   * // Server verifies playerSigned (HS256, game's shared secret)
   * // before trusting the request.
   * ```
   *
   * @throws {Error} If the SDK is not initialized or the request fails.
   */
  getPlayerSigned(): Promise<{
    player: {
      playerId: string;
      registered: boolean;
      username: string | null;
      avatarUrl: string | null;
    };
    playerSigned: string;
  }>;

  /**
   * @internal
   *
   * Gets a state value from the local state
   * @param key - The state key to retrieve
   * @returns The state value, or undefined if not found
   * @throws {Error} If the player is not initialized.
   * @example
   * ```typescript
   * const score = sdk.getPlayerDataVal("score");
   * console.log(score);
   * ```
   */
  getPlayerDataVal(key: string): unknown;

  /**
   * @internal
   *
   * Retrieves the current player state.
   * @returns The current player state object.
   * @throws {Error} If the player is not initialized.
   * @example
   * ```typescript
   * const state = sdk.getPlayerData();
   * console.log(state.score);
   **/
  getPlayerData(): Record<string, unknown>;

  /**
   * @internal
   *
   * Sets a single state value
   * @param key - The state key to set
   * @param value - The value to store
   * @returns void
   * @throws {Error} If the player is not initialized.
   * @example
   * ```typescript
   * sdk.setStateVal("score", 100);
   * ```
   */
  setPlayerDataVal(key: string, value: unknown): void;

  /**
   * @internal
   *
   * Sets multiple state values
   * @param partial - The partial state object to set
   * @returns void
   * @throws {Error} If the player is not initialized.
   *
   * @example
   * ```typescript
   * sdk.setPlayerData({ score: 100, level: 2 });
   * ```
   */
  setPlayerData(partial: Record<string, unknown>): void;

  /**
   * Reports loading progress (0–100) to the platform loading overlay.
   *
   * Only takes effect when the game's loading screen mode is set to
   * "Manual" in the Developer Console. In Manual mode, the overlay is
   * shown automatically on game entry; the game is responsible for
   * progress and dismissal.
   *
   * - Values outside 0–100 are clamped; non-integers are rounded.
   * - Reaching 100 dismisses the overlay with a fade-out.
   * - Safety timeout: if no progress update is received for 15 seconds,
   *   the platform exits the player to the home screen. Each call
   *   resets this timer.
   *
   * Docs: https://docs.jest.com/sdk/loading-screen?engine=html5
   *
   * @param progress - Loading progress from 0 to 100.
   */
  setLoadingProgress(progress: number): void;

  /**
   * Signals to the Jest platform that the game is ready to be played —
   * all assets have loaded, initialization is complete, and the player
   * can now interact.
   *
   * For games in the Jest Fund, this is also an important signal that
   * lets the platform analyze the behavior of the traffic it sends to
   * the game.
   *
   * Call it the moment the player can start playing. Do not wait for
   * optional or user-gated post-init asset downloads; including that time
   * would inflate the measurement with the player's own reaction time.
   * Stream such assets in the background instead.
   *
   * Safe to call at any time, regardless of loading-screen mode; calls
   * after the first are no-ops.
   *
   * Also dismisses the manual loading overlay, as if
   * {@link setLoadingProgress} had been called with `100` — unless the game
   * already drove progress to 100 itself.
   */
  markGameLoaded(): void;

  /**
   * @deprecated Does nothing. The platform no longer collects a
   * first-milestone signal, and the call is safe to remove.
   */
  markFirstMilestone(): void;

  /**
   * Opens the platform's built-in registration popup.
   *
   * The flow is completed via SMS/RCS, then the player is redirected
   * back into the game. Converting guests to registered players is
   * critical for retention — registered players can receive
   * notifications and won't lose progress when the session ends.
   *
   * Use this for the simplest integration; use
   * {@link showRegistrationOverlay} for a fully custom UI.
   *
   * The platform also triggers automatic registration prompts at
   * escalating intervals; opt out via `init({ autoLoginReminders: false })`.
   *
   * @param opts.entryPayload - Optional metadata embedded in the login link.
   *   Available via {@link getEntryPayload} after the player returns.
   *   Useful for tracking where login was initiated.
   * @returns A promise that resolves once the login popup is dismissed.
   *   Resolves immediately if the player is already registered.
   * @throws {Error} If the SDK is not initialized or the entry payload is
   *   invalid.
   */
  login(opts?: { entryPayload?: Record<string, unknown> }): Promise<void>;

  /**
   * Shows a minimal registration overlay and returns action handlers
   * for the game to wire into its own UI.
   *
   * This is the customizable alternative to {@link login}. The platform
   * still renders the required legal text and a close button, but the
   * game owns the rest of the UI (e.g. positioning, copy, buttons).
   *
   * @param opts.theme - "light" or "dark". Defaults to "dark".
   * @param opts.onClose - Called when the overlay is dismissed
   *   (either via the built-in close button or `closeButtonAction`).
   * @param opts.entryPayload - Optional metadata embedded in the login
   *   link, available via {@link getEntryPayload} after registration.
   * @param opts.message - Optional text the player's messaging app is
   *   pre-filled with, in place of the platform's default wording. Must
   *   contain `{{registrationCode}}` exactly once, with a space or
   *   punctuation around it — the platform swaps in a one-time code there,
   *   and that code is how the reply is matched back to this player. Keep the
   *   message under 140 characters so it stays a single text. Emoji and
   *   accented characters cost far more room, so past 70 characters they are
   *   dropped from the pre-filled text rather than splitting it in two.
   * @returns Two functions to wire into your in-game UI:
   *   - `loginButtonAction()` — starts the platform login flow
   *   - `closeButtonAction()` — closes the overlay
   *
   * @example Wire actions to in-game UI
   * ```typescript
   * if (!JestSDK.getPlayer().registered) {
   *   const { loginButtonAction, closeButtonAction } =
   *     JestSDK.showRegistrationOverlay({
   *       theme: "light",
   *       message: "Let me into Dungeon Crawl! {{registrationCode}} is my code.",
   *       onClose: () => closeGamePopup(),
   *     });
   *
   *   myLoginButton.onclick = loginButtonAction;
   *   myCloseButton.onclick = closeButtonAction;
   * }
   * ```
   *
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  showRegistrationOverlay(opts?: {
    theme?: "light" | "dark";
    onClose?: () => void;
    entryPayload?: Record<string, unknown>;
    message?: string;
  }): {
    loginButtonAction: () => void;
    closeButtonAction: () => void;
  };

  /**
   * @internal
   *
   * Unschedules a previously scheduled notification using the identifier.
   * @param options - Unschedule options.
   * @param options.identifier - The identifier used when scheduling the notification.
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  unscheduleNotification(options: { identifier: string }): void;

  /**
   * Records a custom analytics event for the current player.
   *
   * Retired from the docs. Delivered only for onboardings and games granted
   * the `custom_analytics` entitlement; a no-op everywhere else.
   *
   * @hidden
   *
   * Property values should be JSON-serializable primitives or simple
   * objects. Avoid storing PII or sensitive data in event properties.
   *
   * @param eventName - Name of the event (e.g. `"level_complete"`).
   *   Use stable, lowercase, snake_case names.
   * @param properties - Optional structured data attached to the event
   *   (e.g. `{ level: 5, score: 1200 }`).
   * @param options - Optional immediate sending and transport overrides.
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  captureEvent(
    eventName: string,
    properties?: Record<string, unknown>,
    options?: CaptureEventOptions,
  ): void;

  /**
   * Redirects the player to another game owned by the same team as the
   * calling game. The platform performs the navigation after verifying
   * team ownership; redirects to other teams' games are rejected.
   *
   * @param options.gameSlug - Slug of the target game (the `/g/<slug>`
   *   part of its URL).
   * @param options.entryPayload - Optional metadata delivered to the
   *   target game via {@link getEntryPayload}.
   * @param options.skipGameExitConfirm - When `true`, skips the exit
   *   confirmation dialog. Defaults to `false`.
   * @returns `{ result: "success" }` once navigation begins, or
   *   `{ result: "error", error }` with `"game_not_found"`,
   *   `"different_team"`, or `"internal_error"` (transient; safe to retry).
   *
   * Docs: https://docs.jest.com/sdk/app-redirects?engine=html5
   *
   * @example
   * ```typescript
   * const result = await JestSDK.redirectToTeamGame({
   *   gameSlug: "trivia-battle",
   *   entryPayload: { source: "cross_promo_button" },
   * });
   * if (result.result === "error") {
   *   console.error("Redirect failed:", result.error);
   * }
   * ```
   *
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  redirectToTeamGame(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
    skipGameExitConfirm?: boolean;
  }): Promise<
    | { result: "success" }
    | { result: "error"; error: RedirectToTeamGameErrorCode }
  >;

  /**
   * Waits for the platform to acknowledge the state updates made so far
   *
   * The acknowledgement reports that the platform handled the updates, not
   * that it stored them: an update rejected because another session advanced
   * the player's state is acknowledged as well.
   *
   * @internal
   *
   * @async
   * @returns Promise<void>
   * @throws {Error} If the SDK is not initialized.
   * @example
   * ```typescript
   * sdk.setPlayerData({ score: 100 });
   * await sdk.flush();
   * ```
   * */
  flush(): Promise<void>;

  /** App visibility and platform exit events. */
  lifecycle: LifecycleModule;

  /** Device vibration — see {@link HapticsModule}. */
  haptics: HapticsModule;

  /** Player data — see {@link PlayerDataModule}. */
  data: PlayerDataModule;

  /** Notifications — see {@link NotificationsModule}. */
  notifications: NotificationsModule;

  /** Referrals — see {@link ReferralsModule}. */
  referrals: ReferralsModule;

  /** Payments — see {@link PaymentsModule}. */
  payments: PaymentsModule;

  /** Rewarded ads — see {@link AdsModule}. */
  ads: AdsModule;

  /** Social - see {@link SocialModule} */
  social: SocialModule;

  /** @internal */
  internal: {
    /**
     * Acquires a lease for a login message that will be reserved for this user.
     * The returned reservation object is then later used with `sendReservedLoginMessage` to trigger sending an sms.
     *
     * This is done to ensure that the message is associated with the current user and will be correctly attributed.
     *
     * @async
     * @param options.message - The message to acquire a lease for.
     * @param options.replyMessage - The message the platform will respond to the user with to complete their registration. If omitted, a default message will be used.
     * @param options.reminderMessage - The message the platform will send to the user as a reminder. If omitted, default message will be used.
     * @param options.entryPayload - Optional additional data to include in the entry payload. This will be accessible as `getEntryPayload()` after login.
     * @param options.targetGameSlug - Optional slug of a public game the `{loginLink}` should log the user into, instead of the onboarding's flagship game.
     * @returns A promise that resolves to either:
     *   - `{ reservation: LoginReservation }` - An opaque reservation object that can be used with `sendReservedLoginMessage` to send the SMS.
     *   - `{ error: "could_not_acquire_lease" }` - Could not acquire a login lease. This happens when an active login lease already exists for the given message.
     *   - `{ error: "invalid_target_game" }` - `targetGameSlug` does not resolve to a public game.
     * @throws {Error} If the SDK is not initialized or arguments are invalid.
     *
     * @example
     * ```typescript
     * const response = await textclub.internal.reserveLoginMessageAsync({ message: "Your code is abc123" });
     * if ("reservation" in response) {
     *   textclub.internal.sendReservedLoginMessage(response.reservation); // trigger a login sms.
     * } else {
     *   console.error(`Failed to create reservation: ${response.error}`);
     * }
     *
     * @internal
     * ```
     */
    reserveLoginMessageAsync(options: {
      message: string;
      replyMessage?: CustomTextMessage;
      reminderMessage?: CustomTextMessage;
      replyMessageRaw?: RichMessage;
      reminderMessageRaw?: RichMessage;
      keywords?: string[];
      entryPayload?: Record<string, unknown>;
      targetGameSlug?: string;
    }): Promise<GetLoginTokenResponse>;

    /**
     * Triggers an SMS link to open the user's messaging app prefilled with the
     * message reserved with `reserveLoginMessageAsync`.
     *
     * @param reservation - The reservation object returned by `reserveLoginMessageAsync`.
     *
     * @internal
     */
    sendReservedLoginMessage(reservation: LoginReservation): void;

    /**
     * Prepares a link that opens the game in the device's external browser,
     * carrying this session's anonymous analytics id across so both sessions
     * are attributed to the same person. An onboarding links to its flagship
     * game; any other game links to itself. Pass `path` to open another page
     * on the platform instead. Open it with `openReservedBrowserContinuationLink`
     * from a tap handler.
     *
     * Whichever page is opened, the link carries the current page's query
     * parameters (campaign and click ids, except the reload nonce and entry
     * payload), the `entryPayload` option, and, for anonymous players, the
     * handoff analytics id. Query parameters already in `path` take precedence
     * over forwarded ones.
     *
     * @param options.entryPayload - Optional data available as `getEntryPayload()` in the target game.
     * @param options.path - Optional root-relative path on the platform's own origin to open
     *   instead of the game, e.g. `"/promo/spring?ref=onboarding"`. It cannot carry a `#fragment`,
     *   which Android intent links cannot pass through, or point at a `/l/` short link, which
     *   redirects off-site with the query. Takes precedence over the flagship game.
     * @example
     * ```ts
     * const result = await JestSDK.internal.reserveBrowserContinuationLink({
     *   path: "/promo/spring",
     *   entryPayload: { from: "onboarding" },
     * });
     * if ("reservation" in result) {
     *   button.onclick = () =>
     *     JestSDK.internal.openReservedBrowserContinuationLink(result.reservation);
     * }
     * ```
     * @returns A promise that resolves to either:
     *   - `{ reservation: BrowserContinuationReservation }` - An opaque reservation for `openReservedBrowserContinuationLink`.
     *   - `{ error: "no_target_game" }` - This onboarding has no linked flagship game.
     *   - `{ error: "invalid_path" }` - `path` does not start with `/`, leaves the platform's
     *     origin, has a fragment, or is a short link.
     *   - `{ error: "could_not_reserve" }` - The link could not be prepared.
     *
     * @internal
     */
    reserveBrowserContinuationLink(options?: {
      entryPayload?: Record<string, unknown>;
      path?: string;
    }): Promise<ReserveBrowserContinuationResponse>;

    /**
     * Opens the link reserved with `reserveBrowserContinuationLink` in the
     * external browser. Call it synchronously from a user gesture.
     *
     * @param reservation - The reservation returned by `reserveBrowserContinuationLink`.
     *
     * @internal
     */
    openReservedBrowserContinuationLink(
      reservation: BrowserContinuationReservation,
    ): void;

    /**
     * Tracks when an SMS modal is shown to the user.
     * This emits a platform-level analytics event.
     *
     * @param event - The event name to track from a supported set of events.
     * @param properties - Optional properties to include with the event.
     *
     * @throws {Error} If the SDK is not initialized.
     *
     * @internal
     */
    captureOnboardingEvent(
      event: OnboardingAnalyticsEvent,
      properties?: Record<string, unknown>,
    ): void;

    /**
     * @internal
     *
     * Redirects the player from the onboarding game to the linked flagship game.
     */
    redirectToFlagshipGame(options?: {
      entryPayload?: Record<string, unknown>;
    }): void;

    /**
     * @internal
     *
     * Redirects the player to the specified game.
     */
    redirectToGame(options: {
      gameSlug: string;
      entryPayload?: Record<string, unknown>;
      skipGameExitConfirm?: boolean;
    }): void;

    /**
     * @internal
     *
     * Opens the privacy policy page in a new window.
     */
    openPrivacyPolicy(): void;

    /**
     * @internal
     *
     * Opens the terms of service page in a new window.
     */
    openTermsOfService(): void;

    /**
     * @internal
     *
     * Opens the copyright page in a new window.
     */
    openCopyright(): void;

    /**
     * @internal
     *
     * Redirects the user to the explore page of the platform.
     */
    redirectToExplorePage(): void;

    /**
     * @internal
     *
     * Runs name validation checks.
     *
     * @param name - The name to validate.
     * @returns A result describing the validation outcome:
     * - `{ status: "valid" }` when the name passes validation.
     * - `{ status: "invalid", validationError: "too_short" | "too_long" | "inappropriate" }`
     *   when the name fails validation.
     *
     * @example
     * ```typescript
     * const result = await JestSDK.internal.validateName("friendly_name");
     * if (result.status === "invalid") {
     *   console.error(result.validationError);
     * }
     * ```
     */
    validateName(name: string): Promise<NameValidationResult>;

    /**
     * @internal
     *
     * Gets the value of a PostHog multivariate feature flag.
     *
     * @param key - The feature flag key.
     * @returns The variant string if the flag is active, or undefined if not found/inactive.
     */
    getFeatureFlag(key: string): Promise<string | undefined>;
  };

  // deprecated

  /**
   * @internal
   *
   * Redirects the player from the onboarding game to the linked flagship game.
   *
   * If the current game is not configured as an onboarding game, a message is
   * alerted and the nothing happens.
   *
   * @param options.entryPayload - Optional additional data to include in the entry payload for the flagship game.
   *
   * @throws {INVALID_ARGUMENTS} If entryPayload is not a valid object.
   * @throws {Error} If the SDK is not initialized.
   *
   * @deprecated Use `internal.redirectToFlagshipGame` instead.
   */
  redirectToFlagshipGame(options?: {
    entryPayload?: Record<string, unknown>;
  }): void;

  /**
   * @internal
   *
   * Redirects the player to the specified game.
   *
   * @param options.gameSlug - The slug of the game to redirect to.
   * @param options.entryPayload - Optional additional data to include in the entry payload for the target game.
   *
   * @throws {INVALID_ARGUMENTS} If gameSlug is not a valid string or entryPayload is not a valid object.
   * @throws {Error} If the SDK is not initialized.
   *
   * @deprecated Use `internal.redirectToGame` instead.
   */
  redirectToGame(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
    skipGameExitConfirm?: boolean;
  }): void;

  /**
   * @internal
   *
   * Opens the privacy policy page in a new window.
   * @example
   * ```typescript
   * sdk.openPrivacyPolicy();
   * ```
   * @deprecated Use `internal.openPrivacyPolicy` instead.
   */
  openPrivacyPolicy(): void;

  /**
   * @internal
   *
   * Opens the terms of service page in a new window.
   * @throws {Error} If the SDK is not initialized.
   * @deprecated Use `internal.openTermsOfService` instead.
   */
  openTermsOfService(): void;

  /**
   * @internal
   *
   * Opens the copyright page in a new window.
   * @throws {Error} If the SDK is not initialized.
   * @deprecated Use `internal.openCopyright` instead.
   */
  openCopyright(): void;
}

/**
 * Player data — a simple key-value store for per-player state,
 * persisted across sessions and devices.
 *
 * Use this when your game does not have its own backend. Data is
 * stored alongside the player record on the Jest platform.
 *
 * Constraints:
 * - Values must be JSON-serializable.
 * - Limited to 1 MB per game per player; further writes fail
 *   until the stored data size is reduced.
 * - Written directly from the client — do NOT store sensitive
 *   information or data requiring strong security guarantees.
 * - Writes are sent as they are made; `flush()` waits for the platform
 *   to acknowledge them.
 *
 * Docs: https://docs.jest.com/sdk/player?engine=html5#user-data
 */
export interface PlayerDataModule {
  /**
   * Returns the value for a key, or `undefined` if not set.
   *
   * @throws {Error} If the SDK is not initialized.
   */
  get(key: string): unknown;

  /**
   * Returns a snapshot of all player data.
   *
   * Modifying the returned object does NOT update stored data;
   * use `set()` to write back.
   *
   * @throws {Error} If the SDK is not initialized.
   */
  getAll(): Record<string, unknown>;

  /**
   * Sets a single key-value pair.
   *
   * @throws {Error} If the SDK is not initialized.
   */
  set(key: string, value: unknown): void;

  /**
   * Shallow-merges multiple key-value pairs into player data.
   *
   * Existing keys not in `partial` are preserved. To remove a key,
   * use `delete()` (or set its value to `undefined`).
   *
   * @throws {Error} If the SDK is not initialized.
   * @example
   * ```typescript
   * JestSDK.data.set({ score: 100, level: 2 });
   * ```
   */
  set(partial: Record<string, unknown>): void;

  /**
   * Deletes a key from player data.
   *
   * @throws {Error} If the SDK is not initialized.
   */
  delete(key: string): void;

  /**
   * Waits for the platform to acknowledge the writes made so far.
   *
   * `set` and `delete` send their update as soon as they are called,
   * unless an earlier update is still unacknowledged, in which case the
   * queued changes coalesce into the next message. This call resolves
   * immediately when nothing is outstanding.
   *
   * The acknowledgement reports that the platform handled the update, not
   * that it stored it: an update the platform rejects because another
   * session advanced the player's state is acknowledged as well.
   *
   * @throws {Error} If the SDK is not initialized.
   */
  flush(): Promise<void>;
}

/**
 * Options for {@link NotificationsModule.scheduleNotification}.
 *
 * Provide exactly one of `scheduledAt` (exact time) or `scheduledInDays`
 * (fuzzy timing).
 */
export type ScheduleNotificationOptions = {
  /**
   * Stable identifier for this notification. Used to replace or
   * unschedule it later. Scheduling with an existing identifier
   * automatically replaces the previous notification.
   */
  identifier: string;

  /**
   * Main body text of the notification.
   */
  body: string;

  /**
   * Optional title; rendered above the body where supported.
   */
  title?: string;

  /**
   * Call-to-action button label. Must be 1–50 characters.
   */
  ctaText: string;

  /**
   * Higher-priority notifications are weighted more heavily when
   * the platform selects which notification to deliver per day.
   * Defaults to `"medium"`.
   */
  priority?: NotificationPriority;

  /**
   * Optional metadata embedded into the notification's link.
   * Available via `JestSDK.getEntryPayload()` when the player taps
   * the notification.
   */
  entryPayload?: Record<string, unknown>;
} & (
  | {
      /**
       * Exact scheduled delivery time. Must be within the next 7 days.
       * Use this for fixed events or deadlines.
       */
      scheduledAt: Date;
      scheduledInDays?: never;
    }
  | {
      scheduledAt?: never;
      /**
       * Days from now to deliver the notification (0–7, inclusive).
       * The platform picks an optimal delivery time within that window
       * for each player. Use 0 to deliver later the same day; it lands
       * 10 minutes from now at the earliest.
       */
      scheduledInDays: number;
    }
) &
  (
    | {
        /**
         * Reference to a pre-approved image from the Developer Console's
         * Assets Library. Falls back to the game's Hero image if missing
         * or unapproved.
         */
        assetReference?: string;
        /**
         * @deprecated Use {@link assetReference} instead.
         */
        imageReference?: string;
        asset?: never;
      }
    | {
        assetReference?: never;
        imageReference?: never;
        /**
         * Image rendered at runtime, either as a base64 data URL (PNG, JPEG,
         * or WebP, the whole encoded URL at most 2 MiB) or as an `https://`
         * URL the platform downloads once (at most 2 MiB, no redirects). A URL is treated
         * as immutable: change the URL to change the image. Stored as a
         * notification asset and moderated before delivery. Cannot be
         * combined with `assetReference` or `imageReference`.
         */
        asset: string;
      }
  );

/**
 * A single reply option on an interactive notification step. The `key` is
 * returned in the gathered picks; a key matching a message's `key` branches the
 * flow there, otherwise it advances to the next message.
 */
type InteractiveNotificationOption = {
  key: string;
  /** Reply-chip label (1–25 characters). */
  label: string;
};

/** A non-terminal message: presents 1–6 reply chips. */
type InteractiveNotificationStepMessage = {
  /** Stable id; also a branch target for options keyed to it. */
  key: string;
  body: string;
  options: InteractiveNotificationOption[];
};

/**
 * A terminal message: ends its branch and is sent as the "continue in browser"
 * link. The last message in a flow must be terminal.
 */
type InteractiveNotificationTerminalMessage = {
  key: string;
  body: string;
  /** CTA label for the link. */
  ctaText: string;
};

type InteractiveNotificationMessage =
  | InteractiveNotificationStepMessage
  | InteractiveNotificationTerminalMessage;

/**
 * Options for {@link ExperimentalNotificationsModule.scheduleInteractiveNotification}.
 *
 * Provide exactly one of `scheduledAt` (exact time) or `scheduledInDays`
 * (fuzzy timing).
 */
export type ScheduleInteractiveNotificationOptions = {
  /** Stable identifier used to replace or unschedule this notification. */
  identifier?: string;
  /**
   * Ordered messages sent one after another as the player taps. The last
   * message must be terminal (no options); terminal messages carry the
   * "continue in browser" link.
   */
  messages: InteractiveNotificationMessage[];
  /** Delivery weight. Defaults to `"medium"`. */
  priority?: NotificationPriority;
  /** Pre-approved image reference for the first message. */
  assetReference?: string;
  /**
   * Metadata embedded into the game link. The gathered picks are added
   * under `interactivePicks` and delivered to the game on open.
   */
  entryPayload?: Record<string, unknown>;
} & (
  | { scheduledAt: Date; scheduledInDays?: never }
  | { scheduledAt?: never; scheduledInDays: number }
);

/**
 * Notifications — schedule SMS / RCS / Library re-engagement
 * messages for the current player.
 *
 * Only registered players receive notifications. Check via
 * `getPlayer().registered` before scheduling — calls for guest
 * players are valid but won't be delivered.
 *
 * Delivery: scheduled notifications appear in the platform Library tab.
 * Once per day the platform also selects at most one notification per
 * user across all games to deliver as SMS/RCS, weighted by `priority`.
 * Delivery time is platform-determined per user, respecting compliance
 * (quiet hours, opt-outs).
 *
 * Jest handles consent, opt-outs, and messaging cost subsidization.
 *
 * Docs: https://docs.jest.com/sdk/notifications?engine=html5
 */
export interface NotificationsModule {
  /**
   * Schedules a notification for the current player.
   *
   * Provide either `scheduledAt` (exact time) or `scheduledInDays`
   * (fuzzy timing — the platform picks an optimal delivery time per
   * user), but not both.
   *
   * Scheduling with an existing `identifier` automatically replaces
   * the previous notification — no need to unschedule first.
   *
   * Constraints (out-of-range values throw `INVALID_ARGUMENTS`):
   * - `scheduledAt` must be within the next 7 days
   * - `scheduledInDays` must be an integer 0–7 (inclusive); 0 is later today
   * - `ctaText` must be 1–50 characters
   *
   * Use exact scheduling for fixed events/deadlines. Use fuzzy
   * scheduling for time-of-day-flexible re-engagement (lets the
   * platform optimize delivery).
   *
   * See {@link ScheduleNotificationOptions} for field-level docs.
   *
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   * @example
   * ```typescript
   * // Fuzzy: platform picks optimal time within day 2
   * JestSDK.notifications.scheduleNotification({
   *   identifier: "retention_d2",
   *   scheduledInDays: 2,
   *   body: "Your crops are ready to harvest",
   *   ctaText: "Play",
   *   priority: "medium",
   *   entryPayload: { source: "retention_d2" },
   * });
   * ```
   */
  scheduleNotification(options: ScheduleNotificationOptions): void;

  /**
   * Cancels a previously scheduled notification by identifier.
   *
   * Safe to call with an unknown identifier (no-op).
   *
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  unscheduleNotification(options: { identifier: string }): void;

  /** Experimental, unstable notification APIs. */
  experimental: ExperimentalNotificationsModule;
}

/**
 * Experimental notification APIs under `JestSDK.notifications.experimental`.
 *
 * These are unstable and may change without a major version bump.
 */
export interface ExperimentalNotificationsModule {
  /**
   * Schedules an interactive (multi-message) notification: a sequence of
   * messages with reply chips that advance in the messaging thread as the
   * player taps. The gathered picks are delivered to the game via
   * `entryPayload.interactivePicks` when the player opens the game.
   *
   * Provide either `scheduledAt` or `scheduledInDays` (not both). Tapping a
   * chip whose `key` matches a message's `key` branches to that message,
   * otherwise the flow advances to the next message. A message with no options
   * is terminal — it ends the branch and carries the "continue in browser"
   * link (`ctaText`); the last message must be terminal.
   *
   * See {@link ScheduleInteractiveNotificationOptions} for field-level docs.
   *
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   * @example
   * ```typescript
   * JestSDK.notifications.experimental.scheduleInteractiveNotification({
   *   identifier: "tot_daily",
   *   scheduledInDays: 1,
   *   messages: [
   *     {
   *       key: "q1",
   *       body: "Is it Synthetic or Natural?",
   *       options: [
   *         { key: "synthetic", label: "Synthetic" },
   *         { key: "natural", label: "Natural" },
   *         { key: "done", label: "Play in browser" },
   *       ],
   *     },
   *     { key: "done", body: "Keep guessing in the browser", ctaText: "Play" },
   *   ],
   * });
   * ```
   */
  scheduleInteractiveNotification(
    options: ScheduleInteractiveNotificationOptions,
  ): void;
}

/**
 * Rewarded ads — offer the player a reward for playing another Jest game
 * for 30 seconds.
 *
 * The player earns the reward inside the other game, so your game is closed
 * by then. Call `getIncompleteRewards()` on startup, grant each reward, then
 * call `completeReward()` so it is not delivered again.
 */
export interface AdsModule {
  /**
   * Opens a rewarded ad. On `"started"`, Jest replaces your game with the
   * advertised game; on `"no_fill"`, hide your offer.
   *
   * @example
   * ```typescript
   * const { result } = await JestSDK.ads.showRewardedAd();
   * if (result === "no_fill") {
   *   hideRewardedOffer();
   * }
   * ```
   *
   * @throws {Error} If the SDK is not initialized.
   */
  showRewardedAd: () => Promise<{ result: "started" | "no_fill" }>;

  /**
   * Returns rewards the player earned that your game has not completed yet,
   * oldest first. Earned rewards never expire.
   *
   * @returns
   * - `rewards` — each `{ rewardId, earnedAt }`, where
   *   `earnedAt` is milliseconds since the epoch.
   * - `rewardsSigned` — HS256 JWS for server-side verification, signed
   *   with the game's shared secret. Verified payload shape:
   *   `{ rewards, aud: gameId, sub: playerId }`.
   */
  getIncompleteRewards: () => Promise<{
    rewards: RewardData[];
    rewardsSigned: string;
  }>;

  /**
   * Marks a reward as granted so `getIncompleteRewards()` stops returning
   * it. Grant the reward first: a completed reward is never delivered again.
   *
   * @param opts.rewardId - The `rewardId` from `getIncompleteRewards()`.
   */
  completeReward: (opts: {
    rewardId: string;
  }) => Promise<
    | { result: "success" }
    | { result: "error"; error: "internal_error" | "invalid_reward" }
  >;
}

/**
 * Referrals — invite friends via shareable links and track which
 * conversions came from which campaign.
 *
 * Conversions are grouped by `reference`, a stable campaign key
 * you choose (e.g. `"unlock_party_mode_v1"`, `"share_score"`).
 * Only invited players who complete registration count.
 *
 * Both methods work for guest and registered players.
 *
 * For high-stakes rewards (entitlements, currency), verify
 * `referralsSigned` server-side instead of trusting the client.
 *
 * Docs: https://docs.jest.com/sdk/referrals?engine=html5
 */
export interface ReferralsModule {
  /**
   * Returns all referral conversions for the current player,
   * grouped by `reference`.
   *
   * @returns
   * - `referrals` — map of reference → array of `{ playerId, joinedAt }`
   *   (where `joinedAt` is an ISO 8601 timestamp).
   * - `referralsSigned` — HS256 JWS for server-side verification,
   *   signed with the game's shared secret. Verified payload shape:
   *   `{ referrals, aud: gameId, sub: referrerPlayerId }`.
   */
  listReferrals: () => Promise<{
    referrals: {
      [reference: string]: { playerId: string; joinedAt: string }[];
    };
    referralsSigned: string;
  }>;

  /**
   * Opens the platform share dialog with a referral link.
   *
   * Only opens the dialog — does NOT guarantee the player completes
   * the share. The promise resolves once the dialog closes.
   *
   * @param opts.reference - Stable campaign key for grouping conversions
   *   (e.g. `"unlock_party_mode_v1"`).
   * @param opts.entryPayload - Metadata embedded into the shared link,
   *   delivered to the invited player via `getEntryPayload()`.
   *   Useful for attribution, custom invite context, etc.
   * @param opts.shareTitle - Title shown in the share sheet (platform-dependent).
   * @param opts.shareText - Body text shown in the share sheet.
   * @param opts.onboardingSlug - Optional game slug to route invited
   *   players through an onboarding game first.
   * @param opts.notificationTemplates - Optional templates used to notify
   *   the referrer when invited players convert. Each template applies
   *   above its `minConversionCount` threshold; the server picks the
   *   template with the highest matching threshold and a variant from
   *   within it.
   * @param opts.shareImage - Optional **base64 data URL** of an image (e.g.
   *   from `canvas.toDataURL("image/png")`) to use as the OG image on the
   *   referral's landing page — so messaging-app previews, social cards,
   *   etc. show the personalized image instead of the game's static share
   *   image. The platform decodes, hashes, deduplicates, hosts the bytes
   *   on its CDN, and stores the resulting URL on the referral. Accepted
   *   MIME: `image/png`, `image/jpeg`, `image/webp`. The data URL must be
   *   at most 2 MB.
   * @returns `canceled: true` if the player dismissed the dialog.
   *
   * @example Share + check conversions later
   * ```typescript
   * // When the player taps "Invite friends":
   * await JestSDK.referrals.shareReferralLink({
   *   reference: "unlock_party_mode_v1",
   *   shareTitle: "Come play this with me",
   *   shareText: "Join me — I want to unlock Party Mode.",
   * });
   *
   * // Later (e.g. on resume): check how many invites converted
   * const { referrals } = await JestSDK.referrals.listReferrals();
   * const inviteCount = (referrals["unlock_party_mode_v1"] ?? []).length;
   * if (inviteCount >= 3) {
   *   unlockPartyMode();
   * }
   * ```
   *
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  shareReferralLink: (opts: {
    reference: string;
    entryPayload?: Record<string, unknown>;
    shareTitle?: string;
    shareText?: string;
    onboardingSlug?: string;
    notificationTemplates?: ReferralNotificationTemplate[];
    shareImage?: string;
  }) => Promise<{ canceled: boolean }>;
}

/**
 * Social - create social interactions between players.
 *
 * Get the profile of the current player if available. Only registered
 * users will have a profile and only after they have set it up.
 *
 * In case you need to fill in gaps with bots, we provide avatars generated by
 * the platform. Those are deterministically picked based on the provided username.
 * The avatars can be scaled to the requested size to reduce network usage.
 *
 * Docs: https://docs.jest.com/sdk/social?engine=html5
 */
export interface SocialModule {
  /**
   * Returns the profile of the currently logged in player.
   * @param opts - Options for getting the profile
   * @param opts.avatarSize - The size for width and height of the avatar
   * @returns The profile of the currently logged in player
   * @throws {Error} If the SDK is not initialized.
   */
  getProfile: (opts?: { avatarSize: AvatarSize }) => PlayerProfile | null;

  /**
   * Gets the avatar URL for a bot given its username
   * @param opts - Options for getting the avatar URL
   * @param opts.size - The size for width and height of the avatar in pixels
   * @param opts.username - The username of the bot used as a seed for the avatar
   * @returns The generated avatar URL
   * @example
   * ```typescript
   * const avatarUrl = sdk.getBotAvatar({ size: 128, username: "bot" });
   * ```
   */
  getBotAvatar(opts: { size?: AvatarSize; username: string }): string;

  /**
   * Opens the platform's share sheet for an image — the same sheet the
   * platform's own screenshot button shows, offering chat, the native share
   * sheet and download. The player picks where it goes and writes the
   * caption, so a game can never share on the player's behalf without a tap.
   *
   * @param options.image - Base64 PNG (raw or data URL). Omit it to have the
   * platform capture the canvas, or your registered screenshot provider.
   * @param options.entryPayload - Handed back to your game when a player
   * opens the shared message, so a code or coupon travels with it.
   * @returns `{ canceled: true }` when the sheet closed without sharing,
   * including a player who left the page while the post was still landing,
   * so it is not proof that nothing was posted.
   * @example
   * ```typescript
   * await JestSDK.social.shareImage({
   *   image: couponCanvas.toDataURL("image/png"),
   *   entryPayload: { coupon: "SPRING25" },
   * });
   * ```
   */
  shareImage(options?: {
    image?: string;
    entryPayload?: Record<string, unknown>;
  }): Promise<{ canceled: boolean }>;

  /**
   * Registers a custom screenshot source used when the platform asks the
   * game for a screenshot, replacing the SDK's automatic canvas capture.
   * The provider may be async. Return the screenshot as a base64-encoded PNG
   * (raw or data URL), or null when no screenshot is available (e.g.
   * mid-load or on a sensitive screen). Pass null to unregister the provider
   * and restore automatic capture.
   * @param provider - The screenshot source, or null to unregister.
   * @example
   * ```typescript
   * JestSDK.social.setScreenshotProvider(() => {
   *   return myEngine.captureScreenshotAsBase64Png() ?? null;
   * });
   * ```
   */
  setScreenshotProvider(
    provider: (() => string | null | Promise<string | null>) | null,
  ): void;
}

/**
 * Payments — sell in-game products in USD.
 *
 * Players purchase products you configure in the Developer Console
 * directly in USD on the Jest platform. Jest handles checkout
 * end-to-end with the player.
 *
 * **Purchase lifecycle:**
 * 1. List products via {@link PaymentsModule.getProducts | getProducts}.
 * 2. Start checkout via {@link PaymentsModule.beginPurchase | beginPurchase}.
 * 3. Grant the item to the player.
 * 4. Confirm via {@link PaymentsModule.completePurchase | completePurchase}.
 * 5. On startup, recover incomplete purchases via {@link PaymentsModule.getIncompletePurchases | getIncompletePurchases}.
 *
 * **Critical: always grant before confirming.** If the game crashes
 * after confirming but before granting, the purchase can't be
 * recovered (it's no longer incomplete).
 *
 * **Server-side verification (recommended):** for any grant that
 * affects entitlements or currency, send `purchaseSigned` /
 * `purchasesSigned` to your backend, verify the HS256 JWS with your
 * shared secret, and use `purchaseToken` as an idempotency key.
 *
 * **Sandbox testing:** sandbox users see real product prices in the
 * game UI, but the platform checkout modal makes clear that no charge
 * will be made and the resulting purchase records 0 credits. Such a
 * purchase carries `sandbox: true` — in the SDK payload and in the
 * signed JWS — so your backend can grant the item while keeping test
 * traffic out of revenue reporting. Purchases driven from the Developer
 * Console simulator carry the same flag, at their configured price.
 *
 * Docs: https://docs.jest.com/sdk/payments?engine=html5
 */
export interface PaymentsModule {
  /**
   * Lists products available for purchase, configured in the
   * Developer Console.
   *
   * @returns Array of `{ sku, name, description, price, currency }` where
   *   `price` is in the specified `currency` (typically USD).
   * @throws {Error} If the SDK is not initialized or the request fails.
   */
  getProducts(): Promise<
    Array<{
      sku: string;
      name: string;
      description: string | null;
      price: number;
      currency: string;
    }>
  >;

  /**
   * Starts the platform checkout flow for a product.
   *
   * On success, the returned purchase is **incomplete** — your game
   * must grant the item and then call `completePurchase`.
   *
   * @param options.productSku - SKU from `getProducts()`.
   * @returns One of:
   * - `{ result: "success", purchase, purchaseSigned }` — checkout
   *   completed. Use `purchaseSigned` for server-side verification.
   * - `{ result: "cancel" }` — player canceled the flow.
   * - `{ result: "error", error: "internal_error" }` — transient;
   *   safe to retry.
   * - `{ result: "error", error: "invalid_product" }` — SKU not
   *   available; do NOT retry with the same SKU.
   *
   * May also throw on transient errors (e.g. timeout) — treat
   * thrown errors as retryable.
   *
   * @example Full purchase flow (grant before confirm)
   * ```typescript
   * const result = await JestSDK.payments.beginPurchase({
   *   productSku: "powerup_pack_1",
   * });
   *
   * if (result.result !== "success") {
   *   if (result.result === "error") {
   *     console.error("Purchase failed:", result.error);
   *   }
   *   return;
   * }
   *
   * // Recommended: send result.purchaseSigned to your backend to verify
   * // and grant. Use result.purchase.purchaseToken as an idempotency key.
   * await grantItem(result.purchase.productSku);
   *
   * // Only confirm AFTER granting succeeded.
   * await JestSDK.payments.completePurchase({
   *   purchaseToken: result.purchase.purchaseToken,
   * });
   * ```
   *
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  beginPurchase(options: { productSku: string }): Promise<
    | { result: "success"; purchase: PurchaseData; purchaseSigned: string }
    | { result: "cancel" }
    | {
        result: "error";
        error: "internal_error" | "invalid_product";
      }
  >;

  /**
   * Confirms a purchase, marking it complete on the platform.
   *
   * **Only call this AFTER the item has been durably granted.**
   * If you confirm first and crash before granting, the purchase
   * can't be recovered.
   *
   * @param options.purchaseToken - Token from `beginPurchase` or
   *   `getIncompletePurchases`.
   * @returns `{ result: "success" }` or:
   * - `error: "internal_error"` — transient; retry later. Leaving
   *   the purchase incomplete is safe; it will reappear in
   *   `getIncompletePurchases`.
   * - `error: "invalid_token"` — already confirmed, wrong player,
   *   etc. Do NOT retry with the same token.
   *
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  completePurchase(options: { purchaseToken: string }): Promise<
    | { result: "success" }
    | {
        result: "error";
        error: "internal_error" | "invalid_token";
      }
  >;

  /**
   * Starts the platform checkout flow for a subscription.
   *
   * @param options.subscriptionSku - SKU of the subscription to subscribe to.
   * @returns One of:
   * - `{ result: "success", subscription, subscriptionSigned }` — subscribed.
   * - `{ result: "cancel" }` — player canceled the flow.
   * - `{ result: "error", error }` — see error code for details.
   *
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  beginSubscription(options: { subscriptionSku: string }): Promise<
    | {
        result: "success";
        subscription: SubscriptionData;
        subscriptionSigned: string;
      }
    | { result: "cancel" }
    | {
        result: "error";
        error:
          | "internal_error"
          | "invalid_subscription"
          | "already_subscribed"
          | "guest_not_allowed";
      }
  >;

  /**
   * Returns purchases that started checkout but were never confirmed.
   *
   * **Call this on every startup** to handle purchases that succeeded
   * at checkout but never reached `completePurchase` (e.g. due to a
   * crash, network failure, or app close).
   *
   * For each returned purchase: grant the item (using `productSku`),
   * then call `completePurchase`. The response is capped at 50
   * purchases — if `hasMore` is true, call again until it's false.
   *
   * Use `purchasesSigned` for server-side verification before granting.
   *
   * @example Recovery loop on startup
   * ```typescript
   * let hasMore = true;
   * while (hasMore) {
   *   const result = await JestSDK.payments.getIncompletePurchases();
   *
   *   for (const purchase of result.purchases) {
   *     // Recommended: verify result.purchasesSigned on your backend
   *     await grantItem(purchase.productSku);
   *
   *     await JestSDK.payments.completePurchase({
   *       purchaseToken: purchase.purchaseToken,
   *     });
   *   }
   *
   *   hasMore = result.hasMore;
   * }
   * ```
   */
  getIncompletePurchases(): Promise<{
    hasMore: boolean;
    purchases: Array<PurchaseData>;
    purchasesSigned: string;
  }>;

  /**
   * Lists subscription offers for this game along with the player's
   * current entitlement on each one.
   *
   * Subscriptions are configured per-game in the Developer Console and
   * billed in USD on a recurring cadence (currently monthly only).
   *
   * Each entry's `active` field is `"active"` if the player currently
   * holds the subscription and `"inactive"` otherwise. Grant the
   * entitlement when `active === "active"`.
   *
   * Each entry also carries `trialEligible`: `true` only when the offer
   * has a free trial and this wallet has never subscribed to it, so you
   * can show a "Start free trial" call to action only when it applies.
   *
   * Similarly, `introOffer` is non-null only when the offer has an
   * introductory price and this wallet has never subscribed to it: the
   * player pays `introOffer.price` for the first
   * `introOffer.durationPeriods` billing periods, then the standard
   * `price` applies automatically.
   *
   * **Server-side verification (recommended):** for any grant tied to a
   * subscription, send `signed` to your backend and verify the HS256
   * JWS with your shared game secret. The signed payload carries the
   * same `subscriptions` array.
   *
   * For sandbox users, and in the Developer Console simulator, every
   * entry carries `sandbox: true`: `price` shows as configured, but any
   * subscription started that way bills nothing. A sandbox user never
   * gets `introOffer` or `retentionOffer` — a checkout already forced to
   * 0 carries no discount.
   *
   * For guest players, `subscriptions` is an empty array.
   *
   * @throws {Error} If the SDK is not initialized or the request fails.
   */
  getSubscriptions(): Promise<{
    subscriptions: SubscriptionData[];
    signed: string;
  }>;

  /**
   * Opens a cancellation confirmation dialog for the specified subscription.
   *
   * If the user confirms, the subscription is cancelled at the end of the
   * current billing period (the user retains access until then).
   *
   * @param options.subscriptionSku - The SKU of the subscription to cancel.
   * @returns Result indicating success, user dismissed, or an error.
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  cancelSubscription(options: { subscriptionSku: string }): Promise<
    | { result: "success" }
    | { result: "cancel" }
    | {
        result: "error";
        error:
          | "internal_error"
          | "not_found"
          | "not_active"
          | "guest_not_allowed";
      }
  >;

  /**
   * Applies the subscription's configured retention discount to the player's
   * existing subscription — no checkout, applied instantly.
   *
   * Use this for retention flows: when an entitled player asks to cancel,
   * offer the discount your subscription configures (exposed as
   * `retentionOffer` on `SubscriptionData` while the player is eligible).
   * On success the player's next `retentionOffer.durationPeriods` renewals
   * bill at the discounted price, then the standard price returns
   * automatically — same subscription, no interruption.
   *
   * Each player receives a subscription's retention discount only once,
   * and not during a free trial or while an introductory offer window is
   * still running — ineligible claims fail with `not_eligible`. Repeating
   * the call for an already-claimed subscription re-confirms the same
   * discount and succeeds, so retrying after an error is safe. The returned
   * `subscription` reflects the post-claim state (`retentionOffer` becomes
   * `null`).
   *
   * @param options.subscriptionSku - SKU of the subscription the player holds.
   * @returns Result with the refreshed subscription data, or an error.
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  claimRetentionOffer(options: { subscriptionSku: string }): Promise<
    | {
        result: "success";
        subscription: SubscriptionData;
        subscriptionSigned: string;
      }
    | {
        result: "error";
        error: "internal_error" | "not_eligible" | "guest_not_allowed";
      }
  >;
}

let sdkInstance: Sdk | null = null;
// In-flight init() promise, guarding against concurrent calls while the mock
// bundle loads asynchronously.
let initPromise: Promise<void> | null = null;

/**
 * Ensures the SDK instance is initialized.
 * @throws {Error} If the SDK has not been initialized by calling JestSDK.init().
 */
function ensureInstance(): Sdk {
  if (!sdkInstance) {
    throw new Error("SDK not initialized. Call JestSDK.init() first.");
  }
  return sdkInstance;
}

/** @internal */
export type GetLoginLinkResponse =
  | {
      link: string;
    }
  | {
      error: typeof GetLoginLinkResponseErrors.COULD_NOT_ACQUIRE_LEASE;
    };

/** @internal */
export type LoginReservation = {
  readonly __type: "LoginReservation";
  readonly __token: string;
};

/** @internal */
export type BrowserContinuationReservation = {
  readonly __type: "BrowserContinuationReservation";
  readonly __token: string;
};

/** @internal */
export type ReserveBrowserContinuationResponse =
  | { reservation: BrowserContinuationReservation }
  | { error: "no_target_game" | "invalid_path" | "could_not_reserve" };

/** @internal */
export type GetLoginTokenResponse =
  | {
      reservation: LoginReservation;
    }
  | {
      error:
        | typeof GetLoginLinkResponseErrors.COULD_NOT_ACQUIRE_LEASE
        | typeof GetLoginLinkResponseErrors.INVALID_TARGET_GAME;
    };

// Define the type for the singleton, combining the shared interface with static-like members
export type Jest = JestSDK & {
  /**
   * Initializes the SDK. Must be called before any other SDK method.
   * Safe to call multiple times — subsequent calls return the same
   * ready promise.
   *
   * @example Typical app startup
   * ```typescript
   * await JestSDK.init();
   *
   * const player = JestSDK.getPlayer();
   * if (!player.registered) {
   *   // Optionally prompt registration at the right moment
   * }
   *
   * // Recover any purchases from a previous session that crashed
   * await recoverPurchasesOnStartup();
   *
   * startGame();
   * ```
   */
  init: (opts?: {
    /**
     * When set to false, disables the automatic login reminder popups that appear
     * at escalating intervals for guest users. Manual login via `JestSDK.login()`
     * is unaffected. Defaults to true.
     */
    autoLoginReminders?: boolean;

    /**
     * @deprecated mock is no longer used - it is automatically determined based on whether the app is run inside Jest or as standalone.
     */
    mock?: boolean;
  }) => Promise<void>;
};

/**
 * The global JestSDK singleton — the entry point for all SDK usage in
 * HTML5 games.
 *
 * Available as `window.JestSDK` after loading the SDK script:
 * ```html
 * <script src="https://cdn.jest.com/sdk/latest/jestsdk.js"></script>
 * ```
 *
 * Call `JestSDK.init()` first; then use the methods documented on the
 * {@link JestSDK} interface (e.g. `JestSDK.getPlayer()`,
 * `JestSDK.notifications.scheduleNotification(...)`).
 *
 * The {@link Jest} type describes this singleton's full shape,
 * including the `init` method.
 */
export const JestSDK: Jest = {
  /**
   * Initializes the SDK. Must be called before any other SDK methods.
   * If already initialized, it returns the existing ready promise.
   * @param opts - Configuration options for the SDK.
   * @param opts.mock - Whether to use a mock SDK for testing. Includes a debug menu for simulating player states and interactions. Defaults to false.
   * @returns A promise that resolves when the SDK is ready and player data is available.
   */
  init(
    opts: {
      autoLoginReminders?: boolean;
      mock?: boolean;
    } = {},
  ) {
    // Determine if it's mock based on whether we have a platform sid.
    const params = new URLSearchParams(window?.location?.search);
    const isOnJest = params.get("tce") === "1" || !!params.get("sid");

    if (sdkInstance) {
      return sdkInstance.isReady();
    }
    if (initPromise) {
      return initPromise;
    }

    if (!isOnJest) {
      // Mock mode lazily loads that bundle, so construction is async.
      const pending = Sdk.mock(opts).then((instance) => {
        sdkInstance = instance;
        return instance.isReady();
      });
      // Clear the guard if loading fails so callers can retry init().
      pending.catch(() => {
        if (initPromise === pending) {
          initPromise = null;
        }
      });
      initPromise = pending;
      return pending;
    }

    sdkInstance = new Sdk(opts);
    return sdkInstance.isReady();
  },

  getEntryPayload(): Record<string, unknown> {
    return ensureInstance().getEntryPayload();
  },

  getEntrySource(): EntrySource {
    return ensureInstance().getEntrySource();
  },

  async isReady(): Promise<void> {
    // In mock mode init() loads that bundle asynchronously, so the
    // instance isn't set synchronously. Chain off the in-flight init() promise
    // rather than throwing on the not-yet-assigned instance.
    if (!sdkInstance && initPromise) {
      return initPromise;
    }
    return ensureInstance().isReady();
  },

  getPlayer(): {
    playerId: string;
    registered: boolean;
    username: string | null;
    avatarUrl: string | null;
  } {
    return ensureInstance().getPlayer();
  },

  getPlayerSigned(): Promise<{
    player: {
      playerId: string;
      registered: boolean;
      username: string | null;
      avatarUrl: string | null;
    };
    playerSigned: string;
  }> {
    return ensureInstance().getPlayerSigned();
  },

  getPlayerDataVal(key: string): unknown {
    return ensureInstance().getPlayerDataVal(key);
  },

  getPlayerData(): Record<string, unknown> {
    return ensureInstance().getPlayerData();
  },

  setPlayerDataVal(key: string, value: unknown): void {
    ensureInstance().setPlayerDataVal(key, value);
  },

  setPlayerData(partial: Record<string, unknown>): void {
    ensureInstance().setPlayerData(partial);
  },

  setLoadingProgress(progress: number): void {
    ensureInstance().setLoadingProgress(progress);
  },

  markGameLoaded(): void {
    ensureInstance().markGameLoaded();
  },

  markFirstMilestone(): void {
    ensureInstance().markFirstMilestone();
  },

  login: (opts?: { entryPayload?: Record<string, unknown> }): Promise<void> => {
    return ensureInstance().login(opts);
  },

  showRegistrationOverlay: (opts?: {
    theme?: "light" | "dark";
    onClose?: () => void;
    entryPayload?: Record<string, unknown>;
    message?: string;
  }): {
    loginButtonAction: () => void;
    closeButtonAction: () => void;
  } => {
    return ensureInstance().showRegistrationOverlay(opts);
  },

  unscheduleNotification: (options: { identifier: string }): void => {
    ensureInstance().unscheduleNotification(options);
  },

  captureEvent: (
    eventName: string,
    properties?: Record<string, unknown>,
    options?: CaptureEventOptions,
  ): void => {
    ensureInstance().captureEvent(eventName, properties, options);
  },

  redirectToTeamGame(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
    skipGameExitConfirm?: boolean;
  }): Promise<
    | { result: "success" }
    | { result: "error"; error: RedirectToTeamGameErrorCode }
  > {
    return ensureInstance().redirectToTeamGame(options);
  },

  redirectToFlagshipGame(options?: {
    entryPayload?: Record<string, unknown>;
  }): void {
    ensureInstance().internal.redirectToFlagshipGame(options);
  },

  redirectToGame(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
    skipGameExitConfirm?: boolean;
  }): void {
    ensureInstance().internal.redirectToGame(options);
  },

  openPrivacyPolicy(): void {
    ensureInstance().internal.openPrivacyPolicy();
  },

  openTermsOfService(): void {
    ensureInstance().internal.openTermsOfService();
  },

  openCopyright(): void {
    ensureInstance().internal.openCopyright();
  },

  async flush(): Promise<void> {
    return ensureInstance().flush();
  },

  lifecycle: {
    onHide(listener): () => void {
      return ensureInstance().lifecycle.onHide(listener);
    },

    onShow(listener): () => void {
      return ensureInstance().lifecycle.onShow(listener);
    },

    onExitRequested(listener): () => void {
      return ensureInstance().lifecycle.onExitRequested(listener);
    },
  },

  haptics: {
    vibrate(pattern: number | number[]): boolean {
      return ensureInstance().haptics.vibrate(pattern);
    },

    getCapability(): HapticsCapability {
      return ensureInstance().haptics.getCapability();
    },
  },

  data: {
    get(key: string): unknown {
      return ensureInstance().data.get(key);
    },

    getAll(): Record<string, unknown> {
      return ensureInstance().data.getAll();
    },

    set(keyOrPartial: string | Record<string, unknown>, value?: unknown): void {
      if (typeof keyOrPartial === "string") {
        ensureInstance().data.set(keyOrPartial, value);
      } else {
        ensureInstance().data.set(keyOrPartial);
      }
    },

    delete(key: string): void {
      ensureInstance().data.delete(key);
    },

    flush(): Promise<void> {
      return ensureInstance().data.flush();
    },
  },

  notifications: {
    scheduleNotification: (options): void => {
      ensureInstance().notifications.scheduleNotification(options);
    },

    unscheduleNotification: (options): void => {
      ensureInstance().notifications.unscheduleNotification(options);
    },

    experimental: {
      scheduleInteractiveNotification: (options): void => {
        ensureInstance().notifications.experimental.scheduleInteractiveNotification(
          options,
        );
      },
    },
  },

  payments: {
    getProducts: async () => {
      return ensureInstance().payments.getProducts();
    },

    beginPurchase: async (options) => {
      return ensureInstance().payments.beginPurchase(options);
    },

    completePurchase: async (options) => {
      return ensureInstance().payments.completePurchase(options);
    },

    beginSubscription: async (options) => {
      return ensureInstance().payments.beginSubscription(options);
    },

    getSubscriptions: async () => {
      return ensureInstance().payments.getSubscriptions();
    },

    cancelSubscription: async (options) => {
      return ensureInstance().payments.cancelSubscription(options);
    },
    claimRetentionOffer: async (options) => {
      return ensureInstance().payments.claimRetentionOffer(options);
    },

    getIncompletePurchases: async () => {
      return ensureInstance().payments.getIncompletePurchases();
    },
  },

  ads: {
    showRewardedAd: () => {
      return ensureInstance().ads.showRewardedAd();
    },

    getIncompleteRewards: () => {
      return ensureInstance().ads.getIncompleteRewards();
    },

    completeReward: (opts) => {
      return ensureInstance().ads.completeReward(opts);
    },
  },

  referrals: {
    listReferrals: () => {
      return ensureInstance().referrals.listReferrals();
    },

    shareReferralLink: (opts) => {
      return ensureInstance().referrals.shareReferralLink(opts);
    },
  },

  social: {
    getProfile: (opts?: { avatarSize: AvatarSize }): PlayerProfile | null => {
      return ensureInstance().social.getProfile(opts);
    },

    getBotAvatar: (opts: { size?: AvatarSize; username: string }): string => {
      return ensureInstance().social.getBotAvatar(opts);
    },

    setScreenshotProvider: (
      provider: (() => string | null | Promise<string | null>) | null,
    ): void => {
      return ensureInstance().social.setScreenshotProvider(provider);
    },

    shareImage: (options?: {
      image?: string;
      entryPayload?: Record<string, unknown>;
    }): Promise<{ canceled: boolean }> => {
      return ensureInstance().social.shareImage(options);
    },
  },

  internal: {
    reserveLoginMessageAsync: async (options: {
      message: string;
      keywords?: string[];
      entryPayload?: Record<string, unknown>;
      targetGameSlug?: string;
    }): Promise<GetLoginTokenResponse> => {
      return ensureInstance().internal.reserveLoginMessageAsync(options);
    },

    sendReservedLoginMessage: (reservation: LoginReservation): void => {
      return ensureInstance().internal.sendReservedLoginMessage(reservation);
    },

    reserveBrowserContinuationLink: async (options?: {
      entryPayload?: Record<string, unknown>;
      path?: string;
    }): Promise<ReserveBrowserContinuationResponse> => {
      return ensureInstance().internal.reserveBrowserContinuationLink(options);
    },

    openReservedBrowserContinuationLink: (
      reservation: BrowserContinuationReservation,
    ): void => {
      return ensureInstance().internal.openReservedBrowserContinuationLink(
        reservation,
      );
    },

    captureOnboardingEvent: (
      event: OnboardingAnalyticsEvent,
      properties?: Record<string, unknown>,
    ): void => {
      return ensureInstance().internal.captureOnboardingEvent(
        event,
        properties,
      );
    },

    redirectToExplorePage: (): void => {
      return ensureInstance().internal.redirectToExplorePage();
    },

    openPrivacyPolicy: (): void => {
      return ensureInstance().internal.openPrivacyPolicy();
    },

    openTermsOfService: (): void => {
      return ensureInstance().internal.openTermsOfService();
    },

    openCopyright: (): void => {
      return ensureInstance().internal.openCopyright();
    },

    redirectToFlagshipGame: (options?: {
      entryPayload?: Record<string, unknown>;
    }): void => {
      return ensureInstance().internal.redirectToFlagshipGame(options);
    },

    redirectToGame: (options: {
      gameSlug: string;
      entryPayload?: Record<string, unknown>;
      skipGameExitConfirm?: boolean;
    }): void => {
      return ensureInstance().internal.redirectToGame(options);
    },

    validateName: async (name: string): Promise<NameValidationResult> => {
      return ensureInstance().internal.validateName(name);
    },

    getFeatureFlag: async (key: string): Promise<string | undefined> => {
      return ensureInstance().internal.getFeatureFlag(key);
    },
  },
};

/**
 * @internal
 *
 * The Sdk class is exported for internal tests. Game developers should
 * use the JestSDK singleton instead.
 */
export { Sdk };
